import React, { useCallback } from "react";
import "./style.scss";

interface Props {
  textareaRef: HTMLTextAreaElement | null;
  value: string;
  onChange: (newValue: string) => void;
  className?: string;
}

interface FormatAction {
  label: string;
  title: string;
  prefix: string;
  suffix: string;
  block?: boolean;
}

const ACTIONS: FormatAction[] = [
  { label: "B", title: "Bold (**text**)", prefix: "**", suffix: "**" },
  { label: "I", title: "Italic (*text*)", prefix: "*", suffix: "*" },
  { label: "S", title: "Strikethrough (~~text~~)", prefix: "~~", suffix: "~~" },
  { label: "<>", title: "Code (`text`)", prefix: "`", suffix: "`" },
  { label: "```", title: "Code block (```text```)", prefix: "```\n", suffix: "\n```", block: true },
  { label: ">", title: "Quote (> text)", prefix: "> ", suffix: "", block: true },
];

function FormatToolbar({ textareaRef, value, onChange, className }: Props) {
  const applyFormat = useCallback(
    (action: FormatAction) => {
      if (!textareaRef) return;

      const start = textareaRef.selectionStart;
      const end = textareaRef.selectionEnd;
      const selected = value.substring(start, end);

      let newText: string;
      let cursorPos: number;

      if (selected) {
        // Wrap selection
        const wrapped = action.prefix + selected + action.suffix;
        newText = value.substring(0, start) + wrapped + value.substring(end);
        cursorPos = start + wrapped.length;
      } else {
        // Insert placeholder
        const placeholder = action.block ? "" : "text";
        const inserted = action.prefix + placeholder + action.suffix;
        newText = value.substring(0, start) + inserted + value.substring(end);
        cursorPos = start + action.prefix.length + placeholder.length;
      }

      onChange(newText);

      // Restore cursor position after React re-render
      requestAnimationFrame(() => {
        textareaRef.focus();
        if (selected) {
          textareaRef.setSelectionRange(cursorPos, cursorPos);
        } else {
          const selectStart = start + action.prefix.length;
          const selectEnd = cursorPos;
          textareaRef.setSelectionRange(selectStart, selectEnd);
        }
      });
    },
    [textareaRef, value, onChange]
  );

  return (
    <div className={`format-toolbar${className ? ` ${className}` : ""}`}>
      {ACTIONS.map((action) => (
        <button
          key={action.label}
          className={`format-toolbar__btn${action.label === "B" ? " __bold" : ""}${action.label === "I" ? " __italic" : ""}${action.label === "S" ? " __strike" : ""}`}
          onClick={() => applyFormat(action)}
          type="button"
          title={action.title}
        >
          {action.label}
        </button>
      ))}
    </div>
  );
}

export default FormatToolbar;
